import { createSelector } from '@reduxjs/toolkit'
import { findIndex, groupBy, some, transform, flatten, pick } from 'lodash'
import { Answer, CategoryGroupName, Filter, Schema } from 'typings/filters'
import { RootState } from 'reducers/store'
import {
  allCategoryGroupNames,
  generalCategoryGroupNames,
  productReviewCategoryGroupNames,
} from 'config/filters'

export enum Variables {
  TIME_FRAME = 3,
  TIME_FRAME_YEARLY = 706,
  TIME_FRAME_LIVE = 999,
  COUNTRY = 35,
  PRODUCT_GROUP = 12,
  BRAND_ECOSYSTEM = 790,
  BRAND_PURCHASED = 14,
  FEATURE_IMPORTANCE = 162,
  HOME_BAND = 855,
}

const getCategoryGroupNames = (schema: Schema) =>
  schema === Schema.CIE ? generalCategoryGroupNames : productReviewCategoryGroupNames

const unMemoizedGetSelectedFiltersWithSchema = (schema: Schema, selected: Filter[]) =>
  selected.filter((filter) => getCategoryGroupNames(schema).includes(filter.categoryGroup))

const unMemoizedGetAppliedFiltersWithSchema = (
  schema: Schema,
  applied: Record<CategoryGroupName, Filter[]>
) => pick(applied, getCategoryGroupNames(schema))

const unMemoizedGetAppliedFiltersWithoutSchema = (applied: Record<CategoryGroupName, Filter[]>) =>
  pick(applied, allCategoryGroupNames)
export const getFilterValues = (state: Pick<RootState, 'filters'>) => {
  return state.filters.values
}

export const getSelectedFilters = (state: Pick<RootState, 'filters'>) => {
  return state.filters.selected
}

export const getDisabledFilter = (state: Pick<RootState, 'filters'>) => {
  return state.filters.disabled.value
}

export const getAppliedFilters = (state: Pick<RootState, 'filters'>) => {
  return state.filters.applied.value
}

export const getSavedFilters = (state: Pick<RootState, 'filters'>) => {
  return state.filters.saved
}

export const getRetailerLostShopperFilters = (state: Pick<RootState, 'filters'>) => {
  return state.filters.lostShoppers
}
export const getDefaultFilters = (state: Pick<RootState, 'filters'>) => {
  return state.filters.defaultFilters
}

export const selectAppliedSavedFilters = createSelector([getSavedFilters], (saved) => {
  return saved.find(({ isApplied }) => !!isApplied)
})

export const selectAppliedFiltersArray = createSelector([getAppliedFilters], (applied) => {
  return flatten(Object.values(applied)).map(({ categoryGroup, desc, label, ...rest }) => rest)
})
const getSelectedVariable = (_: RootState, props: { variableId: number }) => {
  return props.variableId
}

const getSchema = (_: RootState, props: { schema: Schema }) => props.schema

export const selectSelectedFilters = createSelector(
  [getSchema, getSelectedFilters],
  (schema, selected) => {
    return unMemoizedGetSelectedFiltersWithSchema(schema, selected)
  }
)

export const selectAllAppliedFilters = createSelector([getAppliedFilters], (applied) => {
  return flatten(Object.values(unMemoizedGetAppliedFiltersWithoutSchema(applied))).map(
    ({ categoryGroup, desc, label, answer_id, ...rest }) => rest
  )
})

export const selectAppliedFilters = createSelector(
  [getSchema, getAppliedFilters],
  (schema, applied) => {
    return unMemoizedGetAppliedFiltersWithSchema(schema, applied)
  }
)

export const selectSanitizedAppliedFiltersArray = createSelector(
  [getSchema, getAppliedFilters],
  (schema, applied) => {
    return flatten(Object.values(unMemoizedGetAppliedFiltersWithSchema(schema, applied))).map(
      ({ categoryGroup, desc, label, answer_id, ...rest }) => rest
    )
  }
)

export const selectSanitizedAppliedFilters = createSelector(
  [getSchema, getAppliedFilters],
  (schema, applied) => {
    return flatten(Object.values(unMemoizedGetAppliedFiltersWithSchema(schema, applied))).map(
      ({ categoryGroup, desc, label, answer_id, ...rest }) => rest
    )
  }
)

export const selectSanitizedFilters = createSelector(
  [getSchema, getSelectedFilters],
  (schema, selected) =>
    unMemoizedGetSelectedFiltersWithSchema(schema, selected).map((filter) => {
      const { categoryGroup, desc, label, answer_id, ...rest } = filter ?? {}

      return rest
    })
)

export const getVariableSelectedFilterValues = createSelector(
  [getSchema, getSelectedFilters, getSelectedVariable],
  (schema, selected, variableId) => {
    return unMemoizedGetSelectedFiltersWithSchema(schema, selected).filter(
      (filter) => filter.variable_id === variableId
    )
  }
)

export const selectFiltersForTracking = createSelector(
  [getSchema, getSelectedFilters],
  (schema, selected) => {
    return transform(
      unMemoizedGetSelectedFiltersWithSchema(schema, selected),
      (acc: { categoryName: string; optionChosen: string[] }[], curr) => {
        const [categoryName, value] = curr.desc.split(': ')
        const categoryInUse = findIndex(acc, (obj) => obj.categoryName === categoryName)
        categoryInUse < 0
          ? acc.push({ categoryName, optionChosen: [value] })
          : acc[categoryInUse].optionChosen.push(value)
      },
      []
    )
  }
)

export const selectVariableSelectedLabels = createSelector(
  [getSchema, getSelectedFilters],
  (schema, selected) => {
    return new Set(
      unMemoizedGetSelectedFiltersWithSchema(schema, selected).map(
        (filter) => filter.desc.split(':')[0]
      )
    )
  }
)

export const selectVariableCount = createSelector(
  [getSchema, getSelectedFilters],
  (schema, selected) => {
    return new Set(
      unMemoizedGetSelectedFiltersWithSchema(schema, selected).map((filter) => filter.variable_id)
    ).size
  }
)

export const selectFiltersChanged = createSelector(
  [getSchema, getSelectedFilters, selectSanitizedAppliedFiltersArray],
  (schema, selected, applied) => {
    const selectedFiltersWithSchema = unMemoizedGetSelectedFiltersWithSchema(schema, selected)
    if (selectedFiltersWithSchema.length !== applied.length) {
      return true
    }
    let hasChanged = false
    selectedFiltersWithSchema.forEach((selectedFilter) => {
      if (!hasChanged) {
        const isApplied = some(applied, {
          variable_id: selectedFilter.variable_id,
          answer_code: selectedFilter.answer_code,
        })
        if (!isApplied) {
          hasChanged = true
        }
      }
    })
    return hasChanged
  }
)

export const selectVariableValues = createSelector(
  [getFilterValues, getSelectedVariable],
  (values, variableId) => {
    return {
      values: values[variableId] || [],
      variableId,
    }
  }
)

export const selectValuesWithSelected = createSelector(
  [selectVariableValues, getVariableSelectedFilterValues],
  ({ values, variableId }, selected) => {
    const byId = groupBy(values, 'answer_code')

    const selectedData = selected
      .reduce<(Filter | Answer)[]>((pv, cv) => {
        if (!byId[cv.answer_code]) {
          return [...pv, cv]
        }
        return pv
      }, values)
      .slice()

    return variableId === Variables.TIME_FRAME || variableId === Variables.TIME_FRAME_YEARLY
      ? selectedData.sort((a, b) => b.answer_code - a.answer_code)
      : selectedData
  }
)

export const selectDisabledFilter = createSelector(
  [getDisabledFilter, getSelectedVariable],
  (disabled, selected) => {
    return !!disabled[selected]
  }
)
